import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, X, GraduationCap } from "lucide-react";
import formatApiError from "../../lib/apiError";

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => ({ withCredentials: true });

const COUNTRY_CODES = [
  { code: "+91",  label: "+91 India" },
  { code: "+1",   label: "+1 US/Canada" },
  { code: "+44",  label: "+44 UK" },
  { code: "+61",  label: "+61 Australia" },
  { code: "+971", label: "+971 UAE" },
  { code: "+966", label: "+966 Saudi Arabia" },
  { code: "+974", label: "+974 Qatar" },
  { code: "+968", label: "+968 Oman" },
  { code: "+973", label: "+973 Bahrain" },
  { code: "+65",  label: "+65 Singapore" },
  { code: "+60",  label: "+60 Malaysia" },
  { code: "+92",  label: "+92 Pakistan" },
  { code: "+880", label: "+880 Bangladesh" },
];

const EMPTY_FORM = {
  subject_id: "",
  name: "",
  email: "",
  phone_code: "+91",
  phone: "",
  date_of_birth: "",
  gender: "",
};

const GENDERS = [
  { value: "male",   label: "Male"   },
  { value: "female", label: "Female" },
  { value: "others", label: "Others" },
];

function PhoneField({ form, onChange }) {
  return (
    <div className="phone-field">
      <select
        className="field-input phone-code-select"
        value={form.phone_code}
        onChange={(e) => onChange("phone_code")(e)}
        data-testid="fac-phone-code"
      >
        {COUNTRY_CODES.map((c) => (
          <option key={c.code} value={c.code}>{c.code}</option>
        ))}
      </select>
      <input
        className="field-input phone-number-input"
        placeholder="Phone number"
        value={form.phone}
        onChange={(e) => onChange("phone")(e)}
        maxLength={20}
        data-testid="fac-phone-number"
      />
    </div>
  );
}

export default function FacultyPage() {
  const [faculty, setFaculty]       = useState([]);
  const [subjects, setSubjects]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");

  const [modal, setModal]           = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [formErr, setFormErr]       = useState("");
  const [saving, setSaving]         = useState(false);

  const [confirmId, setConfirmId]   = useState(null);
  const [deleting, setDeleting]     = useState(false);

  const onChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [fRes, sRes] = await Promise.all([
        axios.get(`${API}/api/faculty`, ax()),
        axios.get(`${API}/api/subjects`, ax()),
      ]);
      setFaculty(fRes.data);
      setSubjects(sRes.data);
    } catch (e) {
      setError(formatApiError(e, "Failed to load faculty."));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openAdd  = () => { setForm(EMPTY_FORM); setFormErr(""); setModal({ mode: "add", member: null }); };
  const openEdit = (p) => {
    setForm({
      subject_id: p.subject_id,
      name: p.name,
      email: p.email,
      phone_code: p.phone_code || "+91",
      phone: p.phone || "",
      date_of_birth: p.date_of_birth || "",
      gender: p.gender || "",
    });
    setFormErr("");
    setModal({ mode: "edit", member: p });
  };
  const closeModal = () => { setModal(null); setFormErr(""); };

  const handleSave = async () => {
    if (!form.subject_id)    { setFormErr("Please select a subject."); return; }
    if (!form.name.trim())   { setFormErr("Name is required.");        return; }
    if (!form.email.trim())  { setFormErr("Email is required.");       return; }
    setSaving(true); setFormErr("");
    const payload = { ...form, gender: form.gender || null };
    try {
      if (modal.mode === "add") {
        await axios.post(`${API}/api/faculty`, payload, ax());
      } else {
        await axios.put(`${API}/api/faculty/${modal.member.id}`, payload, ax());
      }
      closeModal(); fetchAll();
    } catch (e) {
      setFormErr(formatApiError(e, "Something went wrong."));
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await axios.delete(`${API}/api/faculty/${confirmId}`, ax()); setConfirmId(null); fetchAll(); }
    catch { setConfirmId(null); }
    finally { setDeleting(false); }
  };

  const fmtPhone = (code, num) => (num ? `${code} ${num}` : "");
  const fmtDOB   = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div className="page-wrap">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">Faculty</h1>
          <p className="page-sub">Add and manage faculty members for your college.</p>
        </div>
        <button className="btn-primary" onClick={openAdd} data-testid="add-faculty-btn">
          <Plus size={16} /> Add Faculty
        </button>
      </div>

      {loading ? <div className="table-loading">Loading…</div>
       : error  ? <div className="table-error">{error}</div>
       : (
        <div className="data-card">
          {faculty.length === 0 ? (
            <div className="empty-state">
              <GraduationCap size={32} color="#43E0FF" />
              <p>No faculty yet. Click <strong>Add Faculty</strong> to create one.</p>
            </div>
          ) : (
            <table className="data-table" data-testid="faculty-table">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>#</th>
                  <th>Name</th>
                  <th>Subject</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Date of Birth</th>
                  <th style={{ width: 110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {faculty.map((p, i) => (
                  <tr key={p.id} data-testid={`faculty-row-${p.id}`}>
                    <td className="td-num">{i + 1}</td>
                    <td className="td-name">{p.name}</td>
                    <td className="td-desc">
                      {p.subject_name ? (
                        <span className="subject-pill">
                          {p.subject_code && <span className="subject-pill-code">{p.subject_code}</span>}
                          {p.subject_name}
                        </span>
                      ) : <span className="td-empty">—</span>}
                    </td>
                    <td className="td-desc">{p.email}</td>
                    <td className="td-desc">{fmtPhone(p.phone_code, p.phone) || <span className="td-empty">—</span>}</td>
                    <td className="td-desc">{fmtDOB(p.date_of_birth) || <span className="td-empty">—</span>}</td>
                    <td>
                      <div className="action-btns">
                        <button className="action-edit"   onClick={() => openEdit(p)}        title="Edit"   data-testid={`edit-faculty-${p.id}`}><Pencil size={15} /></button>
                        <button className="action-delete" onClick={() => setConfirmId(p.id)} title="Delete" data-testid={`delete-faculty-${p.id}`}><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Add / Edit Modal */}
      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box modal-box-md" onClick={(e) => e.stopPropagation()} data-testid="faculty-modal">
            <div className="modal-head">
              <h2 className="modal-title">{modal.mode === "add" ? "Add Faculty" : "Edit Faculty"}</h2>
              <button className="modal-close" onClick={closeModal}><X size={18} /></button>
            </div>

            {formErr && <div className="form-error" data-testid="form-error">{formErr}</div>}

            <div className="modal-body">
              <div className="field">
                <label className="field-label">Subject <span className="required">*</span></label>
                <select
                  className="field-input"
                  value={form.subject_id}
                  onChange={onChange("subject_id")}
                  data-testid="fac-subject-select"
                  autoFocus
                >
                  <option value="">Select a subject…</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.code ? `${s.code} — ${s.name}` : s.name}
                    </option>
                  ))}
                </select>
                {subjects.length === 0 && (
                  <p className="field-hint">No subjects available yet. Ask Super Admin to add subjects first.</p>
                )}
              </div>

              <div className="field">
                <label className="field-label">Name <span className="required">*</span></label>
                <input
                  className="field-input"
                  placeholder="Dr. Jane Smith"
                  value={form.name}
                  onChange={onChange("name")}
                  maxLength={120}
                  data-testid="fac-name-input"
                />
              </div>

              <div className="field-row">
                <div className="field">
                  <label className="field-label">Email <span className="required">*</span></label>
                  <input
                    className="field-input"
                    type="email"
                    placeholder="faculty@college.edu"
                    value={form.email}
                    onChange={onChange("email")}
                    data-testid="fac-email-input"
                  />
                  <p className="field-hint">This email will be used for portal login via OTP.</p>
                </div>
                <div className="field">
                  <label className="field-label">Phone</label>
                  <PhoneField form={form} onChange={onChange} />
                </div>
              </div>

              <div className="field-row">
                <div className="field">
                  <label className="field-label">Date of Birth</label>
                  <input
                    className="field-input"
                    type="date"
                    value={form.date_of_birth}
                    onChange={onChange("date_of_birth")}
                    data-testid="fac-dob-input"
                  />
                </div>
                <div className="field">
                  <label className="field-label">Gender</label>
                  <select
                    className="field-input"
                    value={form.gender}
                    onChange={onChange("gender")}
                    data-testid="fac-gender-select"
                  >
                    <option value="">Select…</option>
                    {GENDERS.map((g) => (
                      <option key={g.value} value={g.value}>{g.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="modal-foot">
              <button className="btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="save-faculty-btn">
                {saving ? <span className="btn-spinner" /> : modal.mode === "add" ? "Add Faculty" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {confirmId && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="modal-box modal-box-sm" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm-modal">
            <div className="modal-head">
              <h2 className="modal-title">Delete Faculty Member</h2>
              <button className="modal-close" onClick={() => setConfirmId(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <p className="confirm-text">Are you sure you want to delete this faculty member? Their portal login will also be removed. This action cannot be undone.</p>
            </div>
            <div className="modal-foot">
              <button className="btn-ghost" onClick={() => setConfirmId(null)}>Cancel</button>
              <button className="btn-danger" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-btn">
                {deleting ? <span className="btn-spinner" /> : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
